/**
 * AffiliateSignUpSuccess — confirms a filed affiliate application.
 *
 * Approval is an operator step, so there is no session to land in: the screen
 * states that the WLB team is reviewing the request and offers the two places
 * the applicant can go next. It is reachable only from a successful submit,
 * because on its own it would claim an application that was never filed.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ButtonLink } from '../components/ui/Button';
import { CheckCircleIcon, ClockIcon } from '../components/ui/icons';
import { AffiliatePortalLayout } from '../features/affiliate/AffiliatePortalLayout';
import type { AffiliateSignUpSuccessState } from '../features/affiliate/affiliateFlowState';
import { AFFILIATE_ROUTES } from '../features/affiliate/affiliateRoutes';
import '../components/layout/authFlow.css';
import '../features/affiliate/affiliatePortal.css';

export function AffiliateSignUpSuccess() {
  const { t } = useTranslation();
  const location = useLocation();
  const state = location.state as AffiliateSignUpSuccessState | null;

  if (!state?.submitted) {
    return <Navigate to={AFFILIATE_ROUTES.SIGN_UP} replace />;
  }

  return (
    <AffiliatePortalLayout centered>
      <div className="auth-flow__card affiliate-success">
        <span className="affiliate-success__badge" aria-hidden>
          <CheckCircleIcon size={32} />
        </span>
        <h1 className="auth-flow__heading">{t('affiliate.signUpSuccess.heading')}</h1>
        <p className="auth-flow__subheading">{t('affiliate.signUpSuccess.subheading')}</p>

        <p className="affiliate-auth__notice affiliate-success__notice">
          <ClockIcon size={18} aria-hidden />
          <span>{t('affiliate.signUpSuccess.reviewNotice')}</span>
        </p>

        {state.email ? (
          <p className="affiliate-success__email">
            {t('affiliate.signUpSuccess.emailNotice', { email: state.email })}
          </p>
        ) : null}

        <div className="affiliate-success__actions">
          <ButtonLink to={AFFILIATE_ROUTES.SIGN_IN} fullWidth>
            {t('affiliate.nav.login')}
          </ButtonLink>
          <ButtonLink to={AFFILIATE_ROUTES.LANDING} variant="outline" fullWidth>
            {t('affiliate.signUpSuccess.goHome')}
          </ButtonLink>
        </div>
      </div>
    </AffiliatePortalLayout>
  );
}
